import SkinuvitaSeoHead from "../../src/components/LandingPage/SkinuvitaSeoHead"
import Image from "next/image"
import Link from "next/link"
import { GetStaticProps } from "next"
import { useTranslations } from "next-intl"
import Footer from "src/components/footer"
import NavBarPatients from "src/components/LandingPage/navbars/NavBarPatients"
import StyledText from "src/typography/typography-component"
import Button from "../../src/components/Button/Button"

import { JobListingPage } from "src/types/jobs"
import {
  fetchJobListings,
  formatJobSalary,
  getJobEmploymentTypeLabel,
  getJobListingHref,
  getJobWorkModeLabel,
  REVALIDATE_INTERVAL,
  shadeColor,
} from "src/utils/jobs"
import skColors from "styles/skColors"
import styles from "styles/LandingPage/careers.module.scss"
import Typography from "../../src/typography/typography"
import TypographyComponent from "src/typography/typography-component"
import BehindNavBar from "../../src/components/BehindNavBar/BehindNavBar"
import { ReactNode } from "react"
import ChipComponent from "../../src/komm-ins-team/components/ChipComponent"

const HR_EMAIL = "hr@skinuvita.de"

type CareersPageProps = {
  jobs: JobListingPage[]
  locale: string
}



export default function CareersPage({ jobs, locale }: CareersPageProps) {
  const t = useTranslations("careers")

  const scrollToOpenings = () => {
    document.getElementById("stellen")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const values = [
    {
      title: t("values.items.innovation.title"),
      description: t("values.items.innovation.description"),
    },
    {
      title: t("values.items.shapeTogether.title"),
      description: t("values.items.shapeTogether.description"),
    },
    {
      title: t("values.items.diversity.title"),
      description: t("values.items.diversity.description"),
    },
    {
      title: t("values.items.trust.title"),
      description: t("values.items.trust.description"),
    },
    {
      title: t("values.items.responsibility.title"),
      description: t("values.items.responsibility.description"),
    },
    {
      title: t("values.items.beingHuman.title"),
      description: t("values.items.beingHuman.description"),
    },
  ]

  const benefits = [
    {
      title: t("benefits.items.impactingWork.title"),
      description: t("benefits.items.impactingWork.description"),
    },
    {
      title: t("benefits.items.startupEnergy.title"),
      description: t("benefits.items.startupEnergy.description"),
    },
    {
      title: t("benefits.items.together.title"),
      description: t("benefits.items.together.description"),
    },
    {
      title: t("benefits.items.flexibility.title"),
      description: t("benefits.items.flexibility.description"),
    },  {
      title: t("benefits.items.growth.title"),
      description: t("benefits.items.growth.description"),
    },  {
      title: t("benefits.items.synergy.title"),
      description: t("benefits.items.synergy.description"),
    },
  ]

  return (
    <div className={styles.page}>
      <SkinuvitaSeoHead
        title={t("meta.title")}
        description={t("meta.description")}
        canonicalPath={"/komm-ins-team"}
      />

      <NavBarPatients />
      <BehindNavBar />

      <section className={styles.hero}>
        <Image
          src="/images/join_the_team/team.png"
          alt="Skinuvita Team"
          fill
          priority
          sizes="100vw"
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroInner}>
          {/*<span className={styles.eyebrow}>{t("hero.eyebrow")}</span>*/}
          <StyledText variant="h1" className={styles.heroTitle} color="#ffffff">
            {t("hero.title")}
          </StyledText>
          {/*<StyledText variant="pLarge" className={styles.heroSubtitle} color="#ffffff">*/}
          {/*  {t("hero.subtitle")}*/}
          {/*</StyledText>*/}
          {/*<div className={styles.heroActions}>*/}
          {/*  <Button hug variant={"CTA"} type="button" onClick={scrollToOpenings} >*/}
          {/*    <TypographyComponent variant={}>{t("hero.primaryCta")}</TypographyComponent>*/}
          {/*  </Button>*/}
          {/*  <a href={`mailto:${HR_EMAIL}`} >*/}
          {/*    <Button  variant={"secondaryCTA"}>{t("hero.secondaryCta")}</Button>*/}
          {/*  </a>*/}
          {/*</div>*/}
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionAfter}`}>
        <div className={styles.sectionInner}>
          <div className={styles.textGrid}>
            <StyledText variant="h2" className={styles.sectionHeading} color={skColors.turquoise[600]}>
              {t("about.title")}
            </StyledText>
            <StyledText as={"p"} variant="h3" color={skColors.darkgreen[600]}>
              {t("about.paragraph1")}
            </StyledText>
            {/*<StyledText variant="pLarge" color={skColors.darkgreen[600]}>*/}
            {/*  {t("about.paragraph2")}*/}
            {/*</StyledText>*/}
          </div>
        </div>
      </section>

      <section id="stellen" className={`${styles.section} ${styles.sectionAfter}`}>
        <div className={styles.sectionInner}>
          {jobs.length > 0 ? (
            <div className={styles.textGrid}>
              <div className={styles.openingsDescription}>
                <StyledText variant="h2" className={styles.sectionHeading} color={skColors.turquoise[600]}>
                  {t("openings.title")}
                </StyledText>
                <StyledText variant="pLarge" className={styles.sectionLead} color={skColors.darkgreen[600]}>
                  {t.rich("openings.lead", {
                    mail: (chunks) => (
                      <a style={{ color: skColors.turquoise[500] }} href={`mailto:hr@skinuvita.de`}>
                        {chunks}
                      </a>
                    ),
                  })}
                </StyledText>
              </div>
              <div className={styles.jobFlex}>
                {jobs.map((job) => {
                  const salary = formatJobSalary(job, locale)
                  const employmentType = getJobEmploymentTypeLabel(job.employment_type, locale)
                  const workMode = getJobWorkModeLabel(job.work_mode, locale)
                  const departments = job.departments
                  const applyHref =
                    job.description_external_url || (job.contact_email ? `mailto:${job.contact_email}` : null)

                  return (
                    <a href={getJobListingHref(job.meta.slug)}>
                      <article key={job.id} className={styles.jobCard}>
                        <div className={styles.jobCardHeader}>
                          <StyledText style={{ marginBottom: "16px" }} variant="h4" color={skColors.darkgreen[600]}>
                            {job.title}
                          </StyledText>
                          <div className={styles.departments}>
                            {departments.map((dept, index) => (
                              <ChipComponent color={dept.color} bgColor={dept.background_color}>
                                {dept.name}
                              </ChipComponent>
                            ))}
                          </div>
                        </div>
                        <div className={styles.jobSummary}>
                          <div className={styles.jobMeta}>
                            {workMode && (
                              <StyledText color={skColors.darkgreen[500]} variant={"tags"}>
                                {workMode} / {employmentType}
                              </StyledText>
                            )}
                            {/*{employmentType && (*/}
                            {/*  <span className={`${styles.pill} ${styles.pillMuted}`}>{employmentType}</span>*/}
                            {/*)}*/}
                            {/*{job.location && <span className={styles.pill}>{job.location}</span>}*/}
                          </div>
                          <Image src={"/svg/new_arrow.svg"} alt={"arrow right"} width={24} height={24} />
                        </div>
  
                        {/*<div className={styles.jobCtas}>*/}
                      </article>
                    </a>
                  )
                })}
              </div>

            </div>
          ) : (
            <div className={styles.openingsDescription}>
              <StyledText variant="h2" className={styles.sectionHeading} color={skColors.turquoise[600]}>
                {t("openings.title")}
              </StyledText>
              <StyledText variant="pLarge"  color={skColors.darkgreen[600]}>
                {t.rich("openings.lead", {
                  mail: (chunks) => (
                    <a style={{ color: skColors.turquoise[500] }} href={`mailto:hr@skinuvita.de`}>
                      {chunks}
                    </a>
                  ),
                })}
              </StyledText>
            </div>
          )}
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionAfter}`}>
        <div className={`${styles.sectionInner} ${styles.sectionFlex}`}>
          <StyledText variant="h2" className={styles.sectionHeading} color={skColors.turquoise[600]}>
            {t("values.title")}
          </StyledText>
          <div className={styles.cardGrid}>
            {values.map((value) => (
              <article key={value.title} className={styles.card}>
                <div className={styles.valueTitle}>
                  <StyledText variant="h4" color={skColors.red[400]}>
                    {value.title}
                  </StyledText>
                  <Image
                    src={"/svg/starIcon.svg"}
                    alt={"star icon"}
                    width={24}
                    height={24}
                    style={{ position: "absolute", top: "-8px", right: "-8px" }}
                  />
                </div>
                <StyledText variant="pRegular" className={styles.cardText} color={skColors.darkgreen[500]}>
                  {value.description}
                </StyledText>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionLast}`}>
        <div className={`${styles.sectionInner} ${styles.sectionFlex}`}>
          <StyledText variant="h2" className={styles.sectionHeading} color={skColors.turquoise[600]}>
            {t("benefits.title")}
          </StyledText>

          <div className={styles.cardGrid}>
            {benefits.map((benefit) => (
              <article key={benefit.title} className={styles.card}>
                <div className={styles.benefitsTitle}>
                  <StyledText variant="h4" color={skColors.blue[500]}>
                    {benefit.title}
                  </StyledText>
                  <Image
                    src={"/svg/check_badge.svg"}
                    alt={"check badge icon"}
                    width={24}
                    height={24}
                    style={{ position: "absolute", top: "-6px", right: "-6px" }}
                  />
                </div>
                <StyledText variant="pRegular" className={styles.cardText} color={skColors.darkgreen[600]}>
                  {benefit.description}
                </StyledText>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export const getStaticProps: GetStaticProps<CareersPageProps> = async (context) => {
  const locale = (context.locale as string) || "de"

  let messages
  try {
    messages = (await import(`../../src/intl/${locale}.json`)).default
  } catch (error) {
    messages = (await import(`../../src/intl/de.json`)).default
  }

  let jobs: JobListingPage[] = []
  try {
    const response = await fetchJobListings()
    console.log(response)
    jobs = response.items
    console.log("jobs")
    console.log(jobs)
  } catch (error) {
    console.error("Failed to fetch job listings", error)
  }

  return {
    revalidate: 1,
    props: {
      jobs,
      messages,
      locale,
    },
  }
}
